import { Component, type ReactNode } from 'react';
import { Button } from '@/components/ui/button';

interface State {
  error: Error | null;
}

/**
 * Top-level React error boundary. Routes inside the App tree are wrapped
 * once; per-feature boundaries can be added for finer-grained recovery.
 */
export class ErrorBoundary extends Component<{ children: ReactNode }, State> {
  override state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  override componentDidCatch(error: Error, info: React.ErrorInfo): void {
    // Phase 2 hooks this into the backend error logger.
    console.error('[ErrorBoundary]', error, info);
  }

  reset = () => this.setState({ error: null });

  override render() {
    if (!this.state.error) return this.props.children;
    return (
      <div className="flex min-h-screen items-center justify-center bg-surface px-margin-mobile">
        <div className="max-w-md text-center">
          <h1 className="mb-4 font-headline-md text-headline-md text-primary">
            Something went wrong
          </h1>
          <p className="mb-8 font-body-md text-body-md text-on-surface-variant">
            We hit an unexpected error. Try refreshing — if it persists please contact us.
          </p>
          <Button onClick={this.reset}>Try again</Button>
        </div>
      </div>
    );
  }
}
